import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@/lib/toast';
import { api } from '@/lib/api';
import { errToToast } from '@/lib/humanizeApiError';
import { useAuthStore } from '@/stores/authStore';
import { useDataStore } from '@/stores/dataStore';
import { Switch } from '@/components/ui/Switch';

/**
 * "Save read history" toggle for the Privacy settings tab. Auto-saves on
 * change via PATCH /api/me. When turned off the server stops persisting this
 * user's read markers (and wipes existing ones), so there's no cross-device
 * read state and no read receipts to others.
 */
export function ReadHistoryToggle() {
  const { t } = useTranslation();
  const me = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const updateCachedUser = useDataStore((s) => s.updateCachedUser);
  const [saving, setSaving] = useState(false);

  // Default ON — matches the server default and the historical behaviour.
  const checked = me?.saveReadHistory ?? true;

  async function change(next: boolean) {
    if (saving) return;
    setSaving(true);
    try {
      const updated = await api.updateMe({ saveReadHistory: next });
      setUser(updated);
      updateCachedUser(updated);
    } catch (err) {
      toast.error(...errToToast(err, t('prof.readHistory.saveFailed')));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Switch
      checked={checked}
      onChange={(next) => void change(next)}
      disabled={saving || !me}
      label={t('prof.readHistory.label')}
    />
  );
}
